import type { StoryboardPromotion, VideoAspectRatio } from '@/store/slices/creativeJobs';

import type { BrandLogoSource } from '../casino-settings/logos';
import type { AdBrand, AdSubject, AdSubjectType, KitCopy } from './banner-kit-api';
import type { CreativeItem } from './creative-api';
import { creativeItemFromGame, type GameSourceRecord } from './game-sources';
import { creativeItemFromFeature, type GamificationFeature } from './gamification-sources';
import { siteOrigin } from './tracking-link';

/**
 * Adapters from the three things an ad can advertise (a promotion banner, a game, a gamification feature) to the
 * {@link AdSubject} snapshotted into a banner kit or mini-game. Games and features go through the same
 * `CreativeItem` adapters the video-ad subject picker uses, so an ad's subject id matches the picker's item id.
 */

/** A promotion banner (or any pool-shaped item) as an ad subject. */
export const adSubjectFromItem = (item: CreativeItem): AdSubject => ({
  id: item.banner_id,
  name: item.internal_name || item.headline || '',
  image_url: item.image_url ?? '',
  headline: item.headline ?? '',
  subtitle: item.subtitle ?? '',
  cta_text: item.cta_text ?? '',
  cta_href: item.cta_href ?? '',
});

/**
 * The full offer a promotion advertises, as one line: its headline plus its subtitle when it has one, joined the
 * way it reads ("100% Welcome Bonus" and "Plus 100 Free Spins" give "100% Welcome Bonus + 100 Free Spins"; a
 * leading "Plus" (or "+", "&", "and", "más", "mais") is dropped for the "+"; an "Up to ..." subtitle continues the headline, and a
 * headline that ends a sentence is followed by the subtitle as the next one). A subtitle the headline already holds
 * is not repeated. This is AD CONTENT (the promotion's own copy, kept verbatim), never translated. Pure.
 */
export const promotionOfferTitle = (subject: Pick<AdSubject, 'headline' | 'subtitle'>): string => {
  const headline = (subject.headline ?? '').trim();
  const subtitle = (subject.subtitle ?? '').trim();
  const extra = subtitle.replace(/^(?:(?:plus|and|más|mas|mais)\s+|[+&]\s*)/i, '').trim();
  if (!extra) return headline;
  if (!headline) return extra;
  if (headline.toLowerCase().includes(extra.toLowerCase())) return headline;
  if (extra !== subtitle) return `${headline} + ${extra}`;
  if (/^up to\b/i.test(extra)) return `${headline} ${extra.charAt(0).toLowerCase()}${extra.slice(1)}`;
  if (/[.!?]$/.test(headline)) return `${headline} ${extra}`;
  return `${headline} + ${extra}`;
};

/** A game as an ad subject: its key art at `aspect` (square by default, the kit hero's shape). */
export const adSubjectFromGame = (game: GameSourceRecord, aspect: VideoAspectRatio = '1:1'): AdSubject =>
  adSubjectFromItem(creativeItemFromGame(game, aspect));

/** A gamification feature as an ad subject (its image is the key art). */
export const adSubjectFromFeature = (feature: GamificationFeature): AdSubject =>
  adSubjectFromItem(creativeItemFromFeature(feature));

/** The call to action a new ad (banner kit, mini-game) starts with when its subject carries none. This is AD
 *  CONTENT drawn into the ad (in the brand's market language, edited in the composer or written by Lumio), not UI
 *  copy, so it is deliberately not translated into the owner's interface language. */
export const DEFAULT_AD_CTA: Record<AdSubjectType, string> = {
  promotion: 'CLAIM BONUS',
  game: 'PLAY NOW',
  gamification: 'JOIN NOW',
};

/** The copy painted into an image banner: the subject's own words, never the owner's (an ad must not promise more than
 *  the live promotion does). Headline: the subject's headline, else its name; sub-line: its subtitle; CTA: its own call
 *  to action, else the subject type's default. Always confirmed: there is nothing for the owner to confirm. Pure. */
export const kitCopyFromSubject = (type: AdSubjectType, subject: AdSubject): KitCopy => ({
  headline: (subject.headline || subject.name || '').trim(),
  subhead: (subject.subtitle ?? '').trim(),
  cta: (subject.cta_text || DEFAULT_AD_CTA[type]).trim(),
  confirmed: true,
});

/** The bare site host of a brand domain (no scheme, no trailing slash), as the legal line's `{domain}` shows it. */
export const brandHost = (brandDomain?: string): string =>
  (brandDomain ?? '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');

/** The logo slot an ad's brand mark comes from when the brand has it: the white wordmark, which reads on the dark
 *  grounds kits and HTML5 banners sit on (mini-games show the primary logo, {@link gameLogos}). */
export const AD_LOGO_SLOT = 'name_horizontal_white';

/** The logo a new ad (kit, HTML5 banner) is prefilled with: the white wordmark of the brand's logo map when it has
 *  one, else `fallback` (the studio's single brand logo). Pure. */
export const adLogoUrl = (logo: BrandLogoSource, fallback?: string): string | undefined => {
  const src = logo && typeof logo === 'object' ? logo[AD_LOGO_SLOT]?.src?.trim() : '';
  return src || fallback;
};

/** The logo slots a mini-game shows: the brand's primary logo (symbol and name) in its head, end card and card backs,
 *  and its bare symbol where only a round mark fits (the offer-wheel hub). The game's surfaces take the logo's own
 *  colours (the runtime reads them from its pixels), so the primary reads whatever its ink. */
export const GAME_LOGO_SLOT = 'symbol_and_name_horizontal';
export const GAME_SYMBOL_SLOT = 'symbol';

/** The brand logos a mini-game is drawn with. */
export interface GameLogos {
  /** The primary logo, else the ad logo ({@link adLogoUrl}: the white wordmark, else the studio's single logo). */
  logo_url: string;
  /** The bare symbol; '' when the brand has none. */
  symbol_url: string;
}

const logoSlotSrc = (logo: BrandLogoSource, slot: string): string =>
  logo && typeof logo === 'object' ? logo[slot]?.src?.trim() ?? '' : '';

/** The brand's mini-game logos from its logo map ({@link GAME_LOGO_SLOT}, {@link GAME_SYMBOL_SLOT}); a brand without a
 *  primary logo keeps the ad logo. Pure. */
export const gameLogos = (logo: BrandLogoSource, fallback?: string): GameLogos => ({
  logo_url: logoSlotSrc(logo, GAME_LOGO_SLOT) || adLogoUrl(logo, fallback) || '',
  symbol_url: logoSlotSrc(logo, GAME_SYMBOL_SLOT),
});

/** Every logo variant of the brand's logo map, as `{slot: url}` (empty when the brand has a single logo url): Image
 *  Banners place the variant that fits each size, in code. Pure. */
export const adLogoSet = (logo: BrandLogoSource): Record<string, string> => {
  if (!logo || typeof logo !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [slot, asset] of Object.entries(logo)) {
    const src = asset?.src?.trim();
    if (src) out[slot] = src;
  }
  return out;
};

/** The brand identity an ad is rendered with. Palette and fonts are filled later (the mini-game config fill). */
export const adBrandFrom = (name: string, domain?: string, logoUrl?: string): AdBrand => ({
  name,
  logo_url: logoUrl ?? '',
  domain: brandHost(domain),
});

/** The subject as the tracking link's promotion list (its `id` matches the pool item, so a promotion resolves its
 *  on-site `cta_href`; a game or feature falls back to the brand homepage). Empty for a subject with no id. */
export const promotionsForTracking = (subject: AdSubject): StoryboardPromotion[] =>
  subject.id
    ? [
        {
          banner_id: subject.id,
          image_url: subject.image_url,
          headline: subject.headline,
          subtitle: subject.subtitle,
          cta_text: subject.cta_text,
        },
      ]
    : [];

/** The absolute landing URL of a subject: its `cta_href` under the brand's origin (kept as is when already
 *  absolute), else the brand homepage; '' when the brand has no domain. Same rule as the tracking link's
 *  destination. */
export const subjectLandingUrl = (subject: Pick<AdSubject, 'cta_href'>, brandDomain?: string): string => {
  const href = subject.cta_href?.trim() ?? '';
  if (/^https?:\/\//i.test(href)) return href;
  const origin = siteOrigin(brandDomain);
  if (!origin) return '';
  if (!href) return origin;
  return `${origin}${href.startsWith('/') ? href : `/${href}`}`;
};
